import React, { useState, useEffect, useCallback } from 'react';
import { t } from '../../i18n';
import { formatMontant, formatDateHeure, libelleVariante } from '../../utils/format';
import { BarChart3, AlertTriangle, TrendingDown, Search, History, Settings, Coins, Tag, Sparkles, Store } from 'lucide-react';
import api from '../../services/api';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import { Button, Input } from '../../components/common/Input';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

const CANAUX_LABELS = {
  magasin: 'Magasin', terrain: 'Terrain', livraison: 'Livraison',
  approvisionnement: 'Approvisionnement', ajustement: 'Ajustement',
};
const TYPES_LABELS = {
  approvisionnement: 'Approvisionnement', vente_magasin: 'Vente magasin',
  sortie_vendeur: 'Sortie vendeur', retour_vendeur: 'Retour vendeur',
  livraison: 'Livraison', annulation_commande: 'Annulation commande',
  reservation_commande: 'Réservation', ajustement: 'Ajustement',
};

export default function Stock() {
  const { user } = useAuth();
  // Ajuster le stock = écriture business → admin exclu (cf. middleware writeOwner)
  const peutAjuster = user?.role === 'proprietaire';
  const [stock, setStock] = useState([]);
  const [resume, setResume] = useState({
    total: 0, ruptures: 0, alertes: 0, ok: 0,
    unites_totales: 0,
    valeur_cout_totale: 0, valeur_vente_totale: 0, marge_potentielle: 0,
  });
  const [mouvements, setMouvements] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [filtreAlerte, setFiltreAlerte] = useState('');
  const [onglet, setOnglet] = useState('stock'); // 'stock' ou 'mouvements'
  // Lot L08 SaaS — sélecteur de boutique pour le propriétaire/admin.
  // '' = vue agrégée (somme sur toutes les boutiques) ; <uuid> = filtre boutique.
  // Pour un gérant_vendeur le serveur force déjà sa boutique : on désactive le selector.
  const [boutiques, setBoutiques] = useState([]);
  const [boutiqueFilter, setBoutiqueFilter] = useState('');
  const peutChoisirBoutique = ['admin', 'proprietaire'].includes(user?.role);
  const [modalAjustement, setModalAjustement] = useState(null);
  const [ajustForm, setAjustForm] = useState({ nouvelle_quantite: '', raison: '' });
  const [ajustLoading, setAjustLoading] = useState(false);

  const chargerStock = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filtreCategorie) params.categorie = filtreCategorie;
      if (filtreAlerte) params.alerte = 'true';
      if (boutiqueFilter) {
        params.boutique_id = boutiqueFilter;
      } else if (peutChoisirBoutique) {
        // « Toutes les boutiques » → on force l'AGRÉGAT (stock cumulé).
        // Sans ça, l'intercepteur API injecte le boutique_id global et la vue
        // serait filtrée sur une seule boutique au lieu d'être cumulée.
        params.boutique = 'all';
      }
      const res = await api.get('/stock', { params });
      setStock(res.data.stock || []);
      setResume(res.data.resume || {});
    } catch (err) {
      // Afficher le vrai message du serveur (403 module désactivé,
      // mot de passe à changer, etc.) plutôt qu'un générique trompeur.
      toast.error(err.response?.data?.message || t('commun.erreurServeur'));
    } finally {
      setLoading(false);
    }
  }, [search, filtreCategorie, filtreAlerte, boutiqueFilter, peutChoisirBoutique]);

  // Charger la liste des boutiques du commerce (pour le sélecteur)
  useEffect(() => {
    if (!peutChoisirBoutique) return;
    api.get('/boutiques')
      .then(r => setBoutiques((r.data || []).filter(b => b.statut === 'actif')))
      .catch(() => setBoutiques([]));
  }, [peutChoisirBoutique]);

  const chargerMouvements = useCallback(async () => {
    try {
      const res = await api.get('/stock/mouvements', { params: { limit: 100 } });
      setMouvements(res.data.mouvements || []);
    } catch {}
  }, []);

  useEffect(() => {
    const timer = setTimeout(chargerStock, 300);
    return () => clearTimeout(timer);
  }, [chargerStock]);

  useEffect(() => {
    if (onglet === 'mouvements') chargerMouvements();
  }, [onglet, chargerMouvements]);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const handleAjustement = async () => {
    if (!ajustForm.nouvelle_quantite && ajustForm.nouvelle_quantite !== 0) {
      return toast.error('Saisissez la nouvelle quantité');
    }
    setAjustLoading(true);
    try {
      await api.post('/stock/ajustement', {
        variante_id: modalAjustement.variante_id,
        nouvelle_quantite: parseInt(ajustForm.nouvelle_quantite),
        raison: ajustForm.raison,
        // Boutique de la ligne (vue détaillée) ou filtre courant —
        // sinon le backend prend la boutique par défaut du commerce.
        boutique_id: modalAjustement.boutique_id || boutiqueFilter || undefined,
      });
      toast.success('Stock ajusté avec succès');
      setModalAjustement(null);
      setAjustForm({ nouvelle_quantite: '', raison: '' });
      chargerStock();
    } catch (err) {
      toast.error(err.response?.data?.message || t('commun.erreurServeur'));
    } finally {
      setAjustLoading(false);
    }
  };

  const couleurNiveau = (n) => ({ rupture: 'red', alerte: 'orange', ok: 'green' })[n] || 'gray';

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div>
        <h1 className="text-2xl font-bold text-white">{t('stock.titre')}</h1>
        <p className="text-gray-400 text-sm mt-0.5">Suivi en temps réel de toutes les variantes</p>
      </div>

      {/* Résumé — disponibilité */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Total références', value: resume.total, icon: BarChart3, color: 'text-blue-400', bg: 'bg-blue-600/10' },
          { label: 'En stock', value: resume.ok, icon: BarChart3, color: 'text-green-400', bg: 'bg-green-600/10' },
          { label: 'Stock faible', value: resume.alertes, icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-600/10' },
          { label: 'Ruptures', value: resume.ruptures, icon: TrendingDown, color: 'text-red-400', bg: 'bg-red-600/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`${bg} border border-gray-800 rounded-xl p-4 flex items-center space-x-3`}>
            <div className={`p-2 rounded-lg ${bg}`}>
              <Icon size={20} className={color} />
            </div>
            <div>
              <p className="text-gray-400 text-xs">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Valorisation du stock — vue financière
          (filtres recherche / catégorie / alertes s'appliquent aussi à ces totaux,
          car le backend les calcule sur la même requête filtrée.) */}
      <div className="bg-gradient-to-br from-yellow-500/5 to-orange-500/5 border border-yellow-600/20 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Coins size={16} className="text-yellow-400" />
          <h2 className="text-sm font-semibold text-yellow-400 uppercase tracking-wide">
            Valorisation du stock
          </h2>
          <span className="text-xs text-gray-500">
            ({Number(resume.unites_totales || 0).toLocaleString('fr-FR')} unités au total)
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Valeur d'achat (coût) */}
          <div
            className="bg-gray-900/60 border border-gray-800 rounded-xl p-4"
            title="Combien tu as PAYÉ pour le stock que tu détiens (qte × prix d'achat moyen). C'est l'argent immobilisé en marchandises."
          >
            <div className="flex items-center gap-2 mb-1">
              <Coins size={16} className="text-orange-400" />
              <p className="text-xs text-gray-400">Valeur d'achat (coût)</p>
            </div>
            <p className="text-orange-400 font-black text-xl">{formatMontant(resume.valeur_cout_totale)}</p>
            <p className="text-xs text-gray-500 mt-0.5">Argent immobilisé en stock</p>
          </div>

          {/* Valeur de vente */}
          <div
            className="bg-gray-900/60 border border-gray-800 rounded-xl p-4"
            title="Combien tu vas ENCAISSER si tu vends tout le stock actuel au prix magasin (qte × prix de vente magasin)."
          >
            <div className="flex items-center gap-2 mb-1">
              <Tag size={16} className="text-green-400" />
              <p className="text-xs text-gray-400">Valeur de vente (magasin)</p>
            </div>
            <p className="text-green-400 font-black text-xl">{formatMontant(resume.valeur_vente_totale)}</p>
            <p className="text-xs text-gray-500 mt-0.5">Si tout est vendu au prix magasin</p>
          </div>

          {/* Marge potentielle */}
          <div
            className="bg-gray-900/60 border border-yellow-600/30 rounded-xl p-4"
            title="Bénéfice théorique si tu vends tout le stock au prix magasin (vente − coût). Avant déduction des dépenses opérationnelles."
          >
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-yellow-400" />
              <p className="text-xs text-gray-400">Marge potentielle</p>
            </div>
            <p className="text-yellow-400 font-black text-xl">{formatMontant(resume.marge_potentielle)}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {resume.valeur_cout_totale > 0
                ? `+${((resume.marge_potentielle / resume.valeur_cout_totale) * 100).toFixed(0)}% sur le coût`
                : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex space-x-1 bg-gray-900 p-1 rounded-xl w-fit">
        {[
          { key: 'stock', label: 'Stock actuel', icon: BarChart3 },
          { key: 'mouvements', label: '📋 Historique', icon: History },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setOnglet(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
              ${onglet === key ? 'bg-yellow-600 text-black' : 'text-gray-400 hover:text-white'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      {onglet === 'stock' && (
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher — produit, modèle, couleur, code..."
              className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500"
            />
          </div>
          {peutChoisirBoutique && boutiques.length > 0 && (
            <div className="relative">
              <Store size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <select
                value={boutiqueFilter}
                onChange={e => setBoutiqueFilter(e.target.value)}
                title="Filtrer le stock par boutique. « Toutes » agrège les quantités sur l'ensemble des boutiques du commerce."
                className="bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-3 py-2.5 text-sm appearance-none"
              >
                <option value="">Toutes les boutiques (agrégé)</option>
                {boutiques.map(b => (
                  <option key={b.id} value={b.id}>{b.nom}</option>
                ))}
              </select>
            </div>
          )}
          <select
            value={filtreCategorie}
            onChange={e => setFiltreCategorie(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5 text-sm"
          >
            <option value="">Toutes catégories</option>
            {categories.map(c => (
              <option key={c.id} value={c.nom}>{c.nom.charAt(0).toUpperCase() + c.nom.slice(1)}</option>
            ))}
          </select>
          <button
            onClick={() => setFiltreAlerte(filtreAlerte ? '' : 'true')}
            aria-pressed={!!filtreAlerte}
            title={filtreAlerte ? 'Filtre actif — clique pour tout réafficher' : 'Afficher seulement les articles en alerte / rupture'}
            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors
              ${filtreAlerte
                ? 'bg-orange-500/15 border-orange-500/50 text-orange-400'
                : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-orange-400'}`}
          >
            <AlertTriangle size={14} />
            <span>Alertes uniquement</span>
          </button>
        </div>
      )}

      {/* Tableau Stock */}
      {onglet === 'stock' && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-gray-900">
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Produit</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Variante</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Disponible</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Réservé</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Seuil</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Statut</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Prix magasin</th>
                  <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase" title="Valeur de vente : qte totale × prix magasin">
                    Valeur stock
                  </th>
                  {peutAjuster && <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Action</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {loading ? (
                  <tr><td colSpan="9" className="text-center py-12">
                    <div className="animate-spin w-8 h-8 border-2 border-yellow-500 border-t-transparent rounded-full mx-auto" />
                  </td></tr>
                ) : stock.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-12 text-gray-500">{t('commun.aucunResultat')}</td></tr>
                ) : stock.map(item => (
                  <tr key={item.variante_id} className="hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{item.produit_nom}</p>
                      <p className="text-gray-500 text-xs">{item.categorie}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        <span className="text-white text-sm">{libelleVariante(item) || <span className="text-gray-500 italic">Standard</span>}</span>
                      </div>
                      <p className="text-gray-600 text-xs font-mono mt-0.5">{item.code_variante}</p>
                    </td>
                    <td className={`px-4 py-3 text-center text-lg font-bold
                      ${item.niveau_stock === 'rupture' ? 'text-red-400'
                        : item.niveau_stock === 'alerte' ? 'text-orange-400'
                        : 'text-green-400'}`}>
                      {item.quantite_disponible}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-400 text-sm">{item.quantite_reservee}</td>
                    <td className="px-4 py-3 text-center text-gray-500 text-sm">{item.seuil_alerte_min}</td>
                    <td className="px-4 py-3 text-center">
                      <Badge variant={couleurNiveau(item.niveau_stock)}>
                        {item.niveau_stock === 'rupture' ? '❌ Rupture'
                          : item.niveau_stock === 'alerte' ? '⚠️ Faible'
                          : '✅ OK'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-yellow-400 text-sm font-medium">
                      {formatMontant(item.prix_vente_magasin)}
                    </td>
                    <td
                      className="px-4 py-3 text-right whitespace-nowrap"
                      title={`Coût d'achat moyen : ${formatMontant(item.prix_achat_moyen || 0)} / unité — Valeur d'achat totale : ${formatMontant(item.valeur_cout || 0)}`}
                    >
                      <div className="text-green-400 text-sm font-bold">
                        {formatMontant(item.valeur_vente_magasin || 0)}
                      </div>
                      <div className="text-gray-500 text-xs">
                        coût {formatMontant(item.valeur_cout || 0)}
                      </div>
                    </td>
                    {peutAjuster && (
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => { setModalAjustement(item); setAjustForm({ nouvelle_quantite: item.quantite_disponible, raison: '' }); }}
                          className="p-2.5 min-h-[40px] min-w-[40px] inline-flex items-center justify-center text-gray-400 hover:text-yellow-400 hover:bg-yellow-600/20 rounded-lg transition-colors"
                          title="Ajuster le stock"
                        >
                          <Settings size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Historique des mouvements */}
      {onglet === 'mouvements' && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-auto max-h-[70vh]">
            <table className="w-full">
              <thead className="sticky top-0 z-10 bg-gray-900">
                <tr className="border-b border-gray-800">
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Produit / Variante</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Canal</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Quantité</th>
                  <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Avant → Après</th>
                  <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Par</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {mouvements.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-12 text-gray-500">Aucun mouvement enregistré</td></tr>
                ) : mouvements.map(m => (
                  <tr key={m.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDateHeure(m.created_at)}</td>
                    <td className="px-4 py-3">
                      <p className="text-white text-sm">{m.produit_nom}</p>
                      <p className="text-gray-500 text-xs">{libelleVariante(m) || 'Standard'}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">{TYPES_LABELS[m.type_mouvement] || m.type_mouvement}</td>
                    <td className="px-4 py-3 text-xs text-gray-400">{CANAUX_LABELS[m.canal] || m.canal}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold text-sm ${m.sens === 'entree' ? 'text-green-400' : 'text-red-400'}`}>
                        {m.sens === 'entree' ? '+' : '-'}{m.quantite}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-xs text-gray-500">
                      {m.quantite_avant} → <span className="text-white font-medium">{m.quantite_apres}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">{m.user_nom || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Ajustement */}
      <Modal open={!!modalAjustement} onClose={() => setModalAjustement(null)} title="Ajustement de stock" size="sm">
        {modalAjustement && (
          <div className="space-y-4">
            <div className="bg-gray-800 rounded-xl p-4 text-sm">
              <p className="text-white font-medium">{modalAjustement.produit_nom}</p>
              <p className="text-gray-400">{libelleVariante(modalAjustement) || 'Standard'}</p>
              <p className="text-yellow-400 mt-2">Stock actuel: <span className="font-bold">{modalAjustement.quantite_disponible}</span></p>
            </div>
            <Input
              label="Nouvelle quantité *"
              type="number"
              min="0"
              value={ajustForm.nouvelle_quantite === 0 || ajustForm.nouvelle_quantite === '0' ? '' : ajustForm.nouvelle_quantite}
              onChange={e => setAjustForm({ ...ajustForm, nouvelle_quantite: e.target.value })}
              placeholder="Ex: 15"
            />
            <Input
              label="Raison de l'ajustement"
              value={ajustForm.raison}
              onChange={e => setAjustForm({ ...ajustForm, raison: e.target.value })}
              placeholder="Ex: Inventaire, article abîmé, don..."
            />
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => setModalAjustement(null)} className="flex-1">
                {t('commun.annuler')}
              </Button>
              <Button loading={ajustLoading} onClick={handleAjustement} className="flex-1">
                Ajuster
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
